import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { getFormSchemaData } from "../../api/form-generator-api";
import LoadingSpinner from "../LoadingSpinner";
import Tab from "../Tab";

import { Type } from "react-feather";
import FormTab from "./FormTab";
import { validatorMap } from "../validations/ValidatorMap";
import { formValidator } from "../../validators";
import { useDispatch } from "react-redux";
import { formGeneratorActions } from "../../store/form-generator/form-generator-slice";

const FormGenerator = forwardRef(({ templateName, Actions }, ref) => {
  const [formSchema, setFormSchema] = useState(null);
  const [schemaValidator, setSchemaValidator] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState(1);
  const dispatch = useDispatch();

  const tabRefs = useRef({});

  const setTabRef = (tabId) => (handle) => {
    if (!handle) {
      delete tabRefs.current[tabId];
      return;
    }
    tabRefs.current[tabId] = handle;
  };

  useImperativeHandle(
    ref,
    () => ({
      getData: () => {
        if (!formSchema?.tabs) return {};
        const all = {};
        for (const t of formSchema.tabs) {
          const tid = String(t.sctName);
          all[tid] = tabRefs.current[tid]?.getState() ?? null;
        }
        return all;
      },
      resetData: () => {
        if (!formSchema?.tabs) return {};

        for (const t of formSchema.tabs) {
          const tid = String(t.sctName);
          tabRefs.current[tid]?.reset();
        }
        dispatch(formGeneratorActions.resetErrors());
      },
      validateSchema: () => {
        if (!formSchema?.tabs) return {};
        let all = {};
        for (const t of formSchema.tabs) {
          const tid = String(t.sctName);
          const tabState = tabRefs.current[tid]?.getFlatState() ?? {};
          all = { ...all, ...tabState };
        }
        const errors = formValidator(all, schemaValidator);
        dispatch(formGeneratorActions.setErrors(errors));
        return errors;
      },
       clearErrors: () => {
      dispatch(formGeneratorActions.resetErrors());
    },
    }),
    [dispatch, formSchema?.tabs, schemaValidator],
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const formSchemaData = await getFormSchemaData(templateName);
      const validationData = formSchemaData?.schemaValidators;
      if (validationData) {
        let mappedValidator = {};
        for (const [field, validationObj] of Object.entries(validationData)) {
          mappedValidator = {
            ...mappedValidator,
            [field]: {
              validators: validatorMap[validationObj.validator],
              name: validationObj.name,
            },
          };
        }
        setSchemaValidator(mappedValidator);
      }
      setFormSchema(formSchemaData);
      setIsLoading(false);
    })();
  }, [templateName]);

  return (
    <>
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          {formSchema && (
            <div style={{ boxShadow: "none" }}>
              <div style={{ boxShadow: "none" }}>
                <>
                  {formSchema.schHasHeading && (
                    <h4 className="ui dividing header">
                      {formSchema.schDescription}
                    </h4>
                  )}
                  {formSchema.tabs && formSchema.tabs.length > 0 && (
                    <>
                      <div className="ui top attached tabular menu">
                        {formSchema.tabs.map((tabComp, i) => (
                          <Tab
                            Icon={Type}
                            authenticated={false}
                            active={tab === i + 1}
                            onClick={() => {
                              setTab(i + 1);
                            }}
                            label={tabComp.sctName}
                            showMandatory={true}
                            isMandatory={tabComp.sctMandatory}
                            resizeForSmallerScreens={true}
                          />
                        ))}
                      </div>
                      {formSchema.tabs.map((tabComp, i) => (
                        <FormTab
                          ref={setTabRef(String(tabComp.sctName))}
                          active={tab === i + 1}
                          tab={tabComp}
                          key={tabComp.sctName}
                        />
                      ))}
                    </>
                  )}
                </>
                <Actions />
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
});

export default FormGenerator;
